import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages } from "./fetchAllPages";
import type { SingleResponse } from "./types";

export interface GroupKey {
  id: string;
  groupKey: string;
  groupId: string;
  groupName?: string;
  description?: string;
  status: string;
  maxDevices: number;
  provisionedCount: number;
  createdAt: string;
  updatedAt: string;
}

export function useGroupKeys() {
  return useQuery({
    queryKey: ["group-keys"],
    // Every key: the page lists and searches them in the browser.
    queryFn: () => fetchAllPages<GroupKey>("/api/v1/companies/me/group-keys"),
    placeholderData: keepPreviousData,
  });
}

export function useCreateGroupKey() {
  const qc = useQueryClient();
  return useMutation({
    // No groupName: the server stores the group's own name.
    mutationFn: (body: { groupId: string; description?: string; maxDevices?: number }) =>
      apiFetch<SingleResponse<GroupKey>>("/api/v1/companies/me/group-keys", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-keys"] }),
  });
}

export function useRevokeGroupKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiFetch<void>(`/api/v1/companies/me/group-keys/${keyId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-keys"] }),
  });
}

// Permanent delete — removes the row. The real API treats DELETE as
// soft-revoke, so we pass `permanent=true` to opt into hard delete.
// Until the backend supports it, this is a dev-only distinction.
export function useDeleteGroupKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiFetch<void>(`/api/v1/companies/me/group-keys/${keyId}?permanent=true`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-keys"] }),
  });
}
